import moment from 'moment'

export interface LocalChatFilesManifestFileData {
    savePath: string
    _lastAccessed?: number
}

export type LocalChatFilesManifestEventType = 'fileAdded' | 'fileRemoved'

export default class LocalChatFilesManifest {
    static MAX_RECORD_LIMIT = 1024
    static LSKEY = 'localDownloadedFilesManifest'

    private static eventTarget = new EventTarget()

    static add(fileUuid: string, data: LocalChatFilesManifestFileData) {
        console.log(`LocalChatFilesManifest: adding record for fileUuid=${fileUuid} data=${JSON.stringify(data)}`)
        let manifest = this.load()

        // Write data at fileUuid
        data._lastAccessed = moment().unix()
        manifest[fileUuid] = data

        // If the manifest got too big, then cull based on data._lastAccessed.
        if (Object.keys(manifest).length > this.MAX_RECORD_LIMIT) {
            manifest = this.cull(manifest)
        }

        this.commit(manifest)

        this.dispatchEvent('fileAdded', fileUuid)
    }

    static addEventListener(
        eventType: LocalChatFilesManifestEventType,
        callback: EventListenerOrEventListenerObject
    ): void {
        this.eventTarget.addEventListener(eventType, callback)
    }

    static removeEventListener(
        eventType: LocalChatFilesManifestEventType,
        callback: EventListenerOrEventListenerObject
    ): void {
        this.eventTarget.removeEventListener(eventType, callback)
    }

    static dispatchEvent(eventType: LocalChatFilesManifestEventType, data: any): void {
        const customEvent = new CustomEvent(eventType, { detail: data })
        this.eventTarget.dispatchEvent(customEvent)
    }

    static get(fileUuid: string): LocalChatFilesManifestFileData | null {
        try {
            const manifest = JSON.parse(localStorage.getItem(this.LSKEY) || '{}')
            if (manifest[fileUuid] == null) {
                return null
            }

            manifest[fileUuid]._lastAccessed = moment().unix()
            this.commit(manifest)

            return manifest[fileUuid] || null
        } catch (e) {
            console.error(`Failed to parse localDownloadedFilesManifest.`)
            console.error(e)
            localStorage.setItem('localDownloadedFilesManifest', '{}')
            return null
        }
    }

    static exists(fileUuid: string): boolean {
        return this.get(fileUuid) != null
    }

    static invalidate(fileUuid: string) {
        const manifest = this.load()
        delete manifest[fileUuid]
        this.commit(manifest)

        this.dispatchEvent('fileRemoved', fileUuid)
    }

    private static load() {
        const manifestStr = localStorage.getItem(this.LSKEY) || '{}'
        let manifest: { [key: string]: LocalChatFilesManifestFileData } = {}
        try {
            manifest = JSON.parse(manifestStr)
        } catch (e) {
            console.error(`Failed to parse localDownloadedFilesManifest.`)
            console.error(e)
            manifest = {}
        }
        return manifest
    }

    private static commit(manifest: { [key: string]: LocalChatFilesManifestFileData }) {
        localStorage.setItem(this.LSKEY, JSON.stringify(manifest))
    }

    private static cull(manifest: { [key: string]: LocalChatFilesManifestFileData }) {
        console.log(
            `LocalChatFilesManifest: manifest length=${Object.keys(manifest).length} ` +
                `hit the MAX_RECORD_LIMIT. Culling the record by the lastAccessed timestamp.`
        )
        const sortedByLastAccessed = Object.keys(manifest)
            .map((k) => ({ k: k, v: manifest[k] }))
            .sort((a, b) => (b.v._lastAccessed || 0) - (a.v._lastAccessed || 0))
        console.log(`LocalChatFilesManifest: sortedByLastAccessed=%O`, sortedByLastAccessed)
        sortedByLastAccessed.length = this.MAX_RECORD_LIMIT

        // convert the sortedList back to the key-value format.
        const manifest_mod: { [key: string]: LocalChatFilesManifestFileData } = {}
        sortedByLastAccessed.forEach((r) => (manifest_mod[r.k] = r.v))
        console.log(`LocalChatFilesManifest: culled manifest=%O`, manifest_mod)
        return manifest_mod
    }
}
